// node_modules
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ToastContext } from "../../App";
import { useCurrentStations } from "../../hooks/useCurrentStations";
import { checkStationAvailability } from "../../utils/checkStationAvailability";
import { formatOrderRestriction } from "../../utils/convertStringToMinutes";
import getSubdomain from "../../utils/getSubdomain";

// assets
import spinner from "../../assets/loader.svg";

// styles
import * as styles from "./StudentHome.module.css";
import * as loaderStyles from "../../components/Loader/Loader.module.css";

function StudentHome({ sendViaWS }) {
  const showToast = useContext(ToastContext);

  useCurrentStations();

  const openedStations = useSelector((state) => state.stations.openedStations);
  const isLoadingStations = openedStations === null;

  const orders = useSelector((state) => state.orders.orders);
  const timezone = useSelector((state) => state.app.timezone);

  const displayStations = (openedStations || []).flatMap((station) => {
    const stationSubStations = station?.sub_stations || [];
    const shouldSplit = !!station?.is_multi;

    if (!shouldSplit || stationSubStations.length === 0) {
      return [
        {
          ...station,
          displayName: station.name,
          displayKey: `station-${station.id}`,
        },
      ];
    }

    return stationSubStations.map((subStation, index) => ({
      ...station,
      displayName: `${station.name} ${subStation?.name || ""}`.trim(),
      displayKey: `station-${station.id}-sub-${subStation?.id || index}`,
      selected_sub_station_id: subStation?.id || null,
      image_name: subStation?.image_name || station.image_name,
    }));
  });

  const stationClick = (e, station) => {
    if (!checkStationAvailability(station, timezone)) {
      e.preventDefault();
      showToast("info", "Station", "Station not available");
      sendViaWS({
        type: "current-stations",
        data: { subdomain: getSubdomain(), time: format(new Date(), "HH:mm") },
      });
    }
    // If station is available, allow the Link navigation to proceed
    // so the `state` (including subStationId) is preserved.
  };

  return (
    <>
      {isLoadingStations ? (
        <div className={loaderStyles.loaderContainer}>
          <img src={spinner} alt="Loading..." className="w-16 h-16" />
        </div>
      ) : displayStations?.length === 0 ? (
        <div>
          <div className={styles.closedPage}>
            <div className={styles.info}>
              <div className={styles.closedTitle}>
                Dine without the Line is not available for ordering
              </div>
              <div className={styles.closedDescription}>
                Our culinary teams are prepping for your next meal. Please check
                back later. Thank you!
              </div>
              {orders?.length ? (
                <Link to="/order-status" className={styles.orderStatusLink}>
                  <i className="pi pi-clock"></i>
                  <div>my orders</div>
                </Link>
              ) : null}
            </div>
            <div className={styles.image}>
              <DotLottieReact src="/images/plate.lottie" loop autoplay />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.pageWrapper}>
          <h2 className={styles.title}>Welcome!</h2>

          <div className={styles.mainWrapper}>
            <p className={styles.description}>Pick your meal station 👇</p>

            <div className={styles.cardsWrapper}>
              {openedStations !== null && openedStations?.length ? (
                displayStations.map((station) => (
                  <Link
                    to={`/make-order/${station.id}`}
                    onClick={(e) => stationClick(e, station)}
                    state={{
                      stationName: station.displayName,
                      subStationId: station.selected_sub_station_id,
                    }}
                    className={styles.cardWrapper}
                    key={station.id}
                  >
                    <div className={styles.stationCard}>
                      <div
                        style={{
                          backgroundImage: `url(${process.env.BACKEND_URL.replace(
                            "/api",
                            "",
                          )}/public/images/${station.image_name})`,
                        }}
                        className={styles.image}
                      >
                        <div className={styles.estimatedTime}>
                          {station.statistics?.estimated_time
                            ? `~ ${station.statistics.estimated_time}`
                            : `~ ${formatOrderRestriction(
                                station.baseline_time || "00:06",
                              )}`}
                        </div>
                      </div>
                      <div className={styles.titleWrapper}>
                        <div>{station.displayName}</div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div>Loading...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StudentHome;
